import { useToast } from '@kk/ui/components/Notifications/Toast';
import {
  type MutationObserverOptions,
  useMutation,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '#/api';
import type { ErrorResponse } from '#/api/api-types';
import queryClient from '#/api/queryClient';
import { getExternalUserInfoQuery } from './useUserInfo';

const method = api.putIdentityEmail;

export type UseUpdateUserEmailResponse = Awaited<ReturnType<typeof method>>;
export type UpdateUserEmailRequest = Required<Parameters<typeof method>[0]>;

export function useUpdateUserEmail(
  options: MutationObserverOptions<
    UseUpdateUserEmailResponse,
    ErrorResponse,
    UpdateUserEmailRequest
  > = {},
) {
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    UseUpdateUserEmailResponse,
    ErrorResponse,
    UpdateUserEmailRequest
  >({
    mutationKey: ['updateUserEmail'],
    mutationFn: (variables) => {
      if (!variables?.email) {
        throw new TypeError('Email is required');
      }
      return method(variables);
    },
    ...options,
    onSuccess: async (data, variables, context, meta) => {
      await queryClient.invalidateQueries({
        queryKey: getExternalUserInfoQuery().queryKey,
      });
      showToast({
        type: 'success',
        headline: t('profilePage.email.updateSuccess'),
        message: t('profilePage.email.updateSuccess.message', {
          email: variables?.email,
        }),
        date: new Date().toISOString(),
        closable: true,
        size: 'long',
      });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context, meta);
      }
    },
    onError: (error, variables, context, meta) => {
      showToast({
        type: 'danger',
        headline: t('profilePage.email.updateError'),
      });

      if (options.onError) {
        options.onError(error, variables, context, meta);
      }
    },
  });
}
